import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post, type Me } from './api';
import { CounterMidDay } from './DayBoard';
import { PhoneThread, StoreMessagesCard } from './Messages';
import type { TimeOffReq } from './TimeOff';
import { Confirm, useToast, type ConfirmSpec } from './ui';

/**
 * The phone Store tab (docs/sign-in.md, prototype "Phone: Avery Kim"): today at one store at a glance.
 * Managers and admins also get Mid-day change, time off waiting, Post week / Post changes and the store's
 * messages; shift leads see who's working and on break; HR looks at every store, view only.
 */

interface StoreToday {
  store: { id: number; name: string }; stores: { id: number; name: string }[]; today: string; now: string; title: string;
  canManage: boolean; canMidDay: boolean; viewOnly: string | null;
  stats: { onClock: number; onBreak: number; toCome: number };
  onBreak: { id: number; name: string; info: string }[]; working: { id: number; name: string; info: string }[];
  need: { hour: string; items: { text: string; state: 'ok' | 'under' | 'over' }[] }[];
  week: { id: number | null; text: string; canPost: boolean; postLabel: string; unposted: number; weekEnd: string; later: string } | null;
  messages: string | null;
}

export function StoreTab({ me, onDesktop }: { me: Me; onDesktop?: () => void }) {
  const [loc, setLoc] = useState<number | null>(() => { try { return Number(localStorage.getItem('mw.storeTab')) || null; } catch { return null; } });
  const [d, setD] = useState<StoreToday | null>(null);
  const [err, setErr] = useState('');
  const [all, setAll] = useState(false);
  const [mid, setMid] = useState(false);
  const [thread, setThread] = useState<number | null>(null);
  const [timeOff, setTimeOff] = useState<TimeOffReq[]>([]);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    try {
      const r = await get<StoreToday>(`/api/store/today${loc ? `?location=${loc}` : ''}`);
      setD(r); setErr('');
      if (r.canManage) {
        const t = await get<{ requests: TimeOffReq[] }>(`/api/time-off?location=${r.store.id}`);
        setTimeOff(t.requests.filter((x) => x.status === 'pending' && x.employeeId !== me.employeeId));
      } else setTimeOff([]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && loc) { setLoc(null); return; }
      setErr(e instanceof ApiError ? e.message : 'Can’t load the store right now.');
    }
  }, [loc, me.employeeId]);
  useEffect(() => { void load(); const x = setInterval(() => void load(), 60_000); return () => clearInterval(x); }, [load]);
  const pick = (id: number) => { setLoc(id); setAll(false); try { localStorage.setItem('mw.storeTab', String(id)); } catch { /* fine */ } };

  if (thread != null) return <PhoneThread employeeId={thread} onBack={() => { setThread(null); void load(); }} />;
  if (!d) return <section className="estore">{err ? <div className="estnote">{err}</div> : null}</section>;

  const decide = (t: TimeOffReq, status: 'approved' | 'denied', kind?: 'unpaid' | 'sick') => {
    const what = status === 'denied' ? 'Deny' : kind === 'sick' ? 'Approve as sick pay' : 'Approve';
    setConfirm({
      title: `${what}: ${t.who}?`,
      text: `${t.who} · ${t.when}${t.note ? ` · “${t.note}”` : ''}.${kind === 'sick' ? ' It goes on the “Sick pay for payroll” list.' : ''}`,
      ok: what,
      run: async () => {
        try { await post(`/api/time-off/${t.id}/decide`, { status, ...(kind ? { kind } : {}) }); showToast(`${what === 'Deny' ? 'Denied' : 'Approved'}: ${t.who}.`); await load(); }
        catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
      },
    });
  };
  const postWeek = () => {
    const w = d.week; if (!w?.id) return;
    const draft = w.postLabel === 'Post week';
    setConfirm({
      title: draft ? 'Post this week?' : 'Post changes?',
      text: draft ? `Everyone at ${d.store.name} will see their shifts for the week ending ${w.weekEnd}.`
        : `${w.unposted} ${w.unposted === 1 ? 'person’s' : 'people’s'} shifts changed since posting. They’ll see the new shifts now, marked “Updated”.`,
      ok: w.postLabel,
      run: async () => {
        try { await post(`/api/schedule/week/${w.id}/${draft ? 'post' : 'post-changes'}`); showToast(draft ? 'Week posted.' : 'Changes posted.'); await load(); }
        catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t post. Try again.'); }
      },
    });
  };
  const working = all ? d.working : d.working.slice(0, 6);

  return (
    <section className="estore">
      <div className="esthead"><h1>{d.title}</h1>
        {d.stores.length > 1 && (
          <select className="sel-sm" value={d.store.id} onChange={(e) => pick(Number(e.target.value))} aria-label="Which store">
            {d.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>
      <div className="estsub">{d.now}</div>
      {d.viewOnly && <div className="estnote">View only · {d.viewOnly}</div>}
      {d.canMidDay && <button className="ebig" onClick={() => setMid(true)}>Mid-day change…</button>}
      <div className="ststats">
        <div className="ststat"><b>{d.stats.onClock}</b><span>on the clock</span></div>
        <div className="ststat"><b>{d.stats.onBreak}</b><span>on break</span></div>
        <div className="ststat"><b>{d.stats.toCome}</b><span>still to come in</span></div>
      </div>
      {d.need.length > 0 && (
        <div className="stcard"><span className="tlabel">Staffing this hour and next</span>
          {d.need.map((x) => <div key={x.hour} className="stneed"><span className="mono">{x.hour}</span>{x.items.map((i) => <span key={i.text} className={'nhave ' + i.state}>{i.text}</span>)}</div>)}
        </div>
      )}
      <div className="stcard"><span className="tlabel">On break now</span>
        {!d.onBreak.length && <span className="stnone">Nobody</span>}
        {d.onBreak.map((x) => <div key={x.id} className="strow"><b>{x.name}</b><span>{x.info}</span></div>)}
      </div>
      <div className="stcard"><span className="tlabel">Working now</span>
        {!d.working.length && <span className="stnone">Nobody else is scheduled today.</span>}
        {working.map((x) => <div key={x.id} className="strow"><b>{x.name}</b><span>{x.info}</span></div>)}
        {d.working.length > 6 && <button className="elink" onClick={() => setAll(!all)} style={{ alignSelf: 'flex-start' }}>{all ? 'Show fewer' : `Show all ${d.working.length}`}</button>}
      </div>
      {d.canManage && (
        <div className="stcard"><span className="tlabel">Time-off requests waiting</span>
          {!timeOff.length && <span className="stnone">None right now</span>}
          {timeOff.map((t) => (
            <div key={t.id} className="stto"><b>{t.who}</b><span className="mono">{t.when}</span>{t.note && <span>{t.note}</span>}
              <div className="sttobtns">
                <button className="tbtn primary" onClick={() => decide(t, 'approved', 'unpaid')}>Approve</button>
                <button className="tbtn" onClick={() => decide(t, 'approved', 'sick')}>Approve as sick pay</button>
                <button className="danger" onClick={() => decide(t, 'denied')}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {d.week && (
        <div className="stcard"><span className="tlabel">This week’s schedule</span><span>{d.week.text}</span>
          {d.week.canPost && <button className="tbtn primary" onClick={postWeek} style={{ alignSelf: 'flex-start' }}>{d.week.postLabel}</button>}
          <span className="stnone">{d.week.later}</span>
        </div>
      )}
      {d.messages && <StoreMessagesCard locationId={me.role === 'hr' ? 'all' : d.store.id} title={d.messages} onOpen={setThread} />}
      {onDesktop && <button className="elink" onClick={onDesktop} style={{ alignSelf: 'center' }}>Open the desktop pages</button>}
      {mid && <CounterMidDay locationId={d.store.id} date={d.today} onClose={() => setMid(false)} onDone={() => { setMid(false); void load(); }} />}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </section>
  );
}
